import type { PriceChangeLine } from "@/lib/manufacturing/priceChangeSnapshot";

function money(value: number | null): string {
  if (value == null) return "";
  return value.toLocaleString("en-US", { style: "currency", currency: "USD" });
}

const cell = "whitespace-nowrap px-2 py-1.5 text-xs text-gray-800 dark:text-surface-ink";
const head = "whitespace-nowrap px-2 py-1.5 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500";

export function ManufacturingModelsPriceChangeTable({
  lines,
  locked,
  page,
  pageCount,
  filteredCount,
  onPage,
  onNewPrice,
  onConfirm,
}: {
  lines: readonly PriceChangeLine[];
  locked: boolean;
  page: number;
  pageCount: number;
  filteredCount: number;
  onPage: (page: number) => void;
  onNewPrice: (recipeId: string, field: "new_wholesale_usd" | "new_msrp_usd", raw: string) => void;
  onConfirm: (recipeId: string, field: "new_wholesale_confirmed" | "new_msrp_confirmed", checked: boolean) => void;
}) {
  return (
    <div className="space-y-2">
      <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-surface-border-strong">
        <table className="min-w-full border-collapse">
          <thead className="bg-gray-50 dark:bg-surface-sunken">
            <tr>
              {["Code", "Name", "Kind", "Group", "Style", "WS now", "MSRP now", "WS COGS", "MSRP COGS", "New WS", "New MSRP"].map(
                (label) => (
                  <th key={label} className={head}>
                    {label}
                  </th>
                ),
              )}
            </tr>
          </thead>
          <tbody>
            {lines.length === 0 ? (
              <tr>
                <td colSpan={11} className={`${cell} py-6 text-center text-gray-500`}>
                  No items match these filters.
                </td>
              </tr>
            ) : (
              lines.map((line) => (
                <tr key={line.recipe_id} className="border-t border-gray-100 dark:border-surface-border">
                  <td className={`${cell} font-medium`}>{line.code}</td>
                  <td className={cell}>{line.name}</td>
                  <td className={cell}>{line.kind}</td>
                  <td className={cell}>{line.group_name}</td>
                  <td className={cell}>{line.roof_style}</td>
                  <td className={cell}>{money(line.current_wholesale_usd)}</td>
                  <td className={cell}>{money(line.current_msrp_usd)}</td>
                  <td className={cell}>{money(line.suggested_wholesale_usd)}</td>
                  <td className={cell}>{money(line.suggested_msrp_usd)}</td>
                  <td className={cell}>
                    <span className="inline-flex items-center gap-1.5">
                      <PriceInput
                        value={line.new_wholesale_usd}
                        locked={locked}
                        onCommit={(raw) => onNewPrice(line.recipe_id, "new_wholesale_usd", raw)}
                      />
                      {line.new_wholesale_usd != null ? (
                        <ConfirmBox
                          checked={line.new_wholesale_confirmed === true}
                          locked={locked}
                          label={`Confirm new wholesale for ${line.code}`}
                          onChange={(checked) => onConfirm(line.recipe_id, "new_wholesale_confirmed", checked)}
                        />
                      ) : null}
                    </span>
                  </td>
                  <td className={cell}>
                    <span className="inline-flex items-center gap-1.5">
                      <PriceInput
                        value={line.new_msrp_usd}
                        locked={locked}
                        onCommit={(raw) => onNewPrice(line.recipe_id, "new_msrp_usd", raw)}
                      />
                      {line.new_msrp_usd != null ? (
                        <ConfirmBox
                          checked={line.new_msrp_confirmed === true}
                          locked={locked}
                          label={`Confirm new MSRP for ${line.code}`}
                          onChange={(checked) => onConfirm(line.recipe_id, "new_msrp_confirmed", checked)}
                        />
                      ) : null}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between text-xs text-gray-600 dark:text-surface-muted">
        <span>
          {filteredCount} shown · page {page + 1} of {pageCount}
        </span>
        <span className="flex gap-2">
          <button type="button" className="underline disabled:opacity-40" disabled={page <= 0} onClick={() => onPage(page - 1)}>
            Previous
          </button>
          <button
            type="button"
            className="underline disabled:opacity-40"
            disabled={page + 1 >= pageCount}
            onClick={() => onPage(page + 1)}
          >
            Next
          </button>
        </span>
      </div>
    </div>
  );
}

function ConfirmBox({
  checked,
  locked,
  label,
  onChange,
}: {
  checked: boolean;
  locked: boolean;
  label: string;
  onChange: (checked: boolean) => void;
}) {
  return (
    <input
      type="checkbox"
      className="h-4 w-4"
      checked={checked}
      disabled={locked}
      aria-label={label}
      title={checked ? "Confirmed" : "Not confirmed"}
      onChange={(event) => onChange(event.target.checked)}
    />
  );
}

function PriceInput({
  value,
  locked,
  onCommit,
}: {
  value: number | null;
  locked: boolean;
  onCommit: (raw: string) => void;
}) {
  if (locked) return <span>{money(value)}</span>;
  return (
    <input
      key={value ?? "blank"}
      defaultValue={value ?? ""}
      inputMode="decimal"
      className="w-24 rounded-md border border-gray-200 bg-white px-2 py-1 text-xs dark:border-surface-border-strong dark:bg-surface-raised"
      onBlur={(event) => onCommit(event.target.value)}
    />
  );
}
